import React, { useEffect, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { ConfigProvider, Flex, Layout, Typography } from 'antd';
import './PageLayout.css';
import { useAuth } from '../../context/AuthContext';
import { Header, SideNavigation } from '../../components';
import BoschStrip from '../../assets/cd_bosch_supergraphic.png';
import { useLocation } from 'react-router-dom';
import { TEST } from '../../utilities/constants';
import '../../pages/DriverMonitoringSystem/DriverMonitoringSystem.css';
import AdminBreadcrumb from '../../components/AdminBreadcrumb/AdminBreadcrumb';
import OrganisationChange from '../OrganisationChange/OrganisationChange';
import { abortPendingRequests } from '../../services';

// Lazy load popups as they're not needed immediately
const DriverDrowsinessPopup = lazy(
  () => import('../DriverDrowsinessPopup/DriverDrowsinessPopup')
);
const SOSPopup = lazy(() => import('../SOSPopup/SOSPopup'));
const AnomalySockets = lazy(() => import('../AnomalySockets/AnomalySockets'));
const InactivityMonitor = lazy(
  () => import('../InactiveMonitoring/InactiveMonitoring')
);

const { Content } = Layout;
const { Text } = Typography;

const PageLayout = ({ children }) => {
  const { isLoggedIn, setIsLoggedIn } = useAuth();
  const isTestEnv = process.env.REACT_APP_ENVIRONMENT === TEST;
  const location = useLocation();
  const shouldBeScrollable =
    location?.pathname?.includes('vehicle-monitoring') ||
    location?.pathname?.includes('broadcast');

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      abortPendingRequests();
    };
  }, [location.pathname]);

  const isMobileView = location.pathname.split('/')[1] === 'mobile';
  const heading =
    location.pathname.split('/')[1] === 'mobile' &&
    location.pathname.split('/')[3];

  useEffect(() => {
    if (isTestEnv) {
      setIsLoggedIn(true);
    }
  }, [isLoggedIn]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Layout: {
            headerPadding: isMobileView ? '0.75rem' : '15px 0px 9px 20px',
            headerHeight: isLoggedIn ? '54px' : 0,
          },
        },
      }}
    >
      <Layout
        className={`page-layout ${shouldBeScrollable && 'non-scrollable-page'}`}
        style={{
          backgroundColor: isMobileView
            ? '#FFFFFF'
            : 'var(--content-bg-color, #000000)',
        }}
      >
        {!isMobileView && (
          <img alt="graphic" src={BoschStrip} className="bosch-strip" />
        )}
        {isLoggedIn &&
          !isMobileView &&
          !location.pathname.includes('super-admin-console') &&
          !location.pathname.includes('platform-operations-console') &&
          !location.pathname.includes('change-password') && <SideNavigation />}
        <Layout
          style={{
            marginLeft:
              isLoggedIn &&
              !isMobileView &&
              !location.pathname.includes('super-admin-console') &&
              !location.pathname.includes('platform-operations-console') &&
              !location.pathname.includes('change-password')
                ? 48
                : 0,
            backgroundColor: isMobileView
              ? '#FFFFFF'
              : 'var(--body-bg-color, #000000)',
          }}
        >
          {isLoggedIn && !location.pathname.includes('change-password') && (
            <Header isMobileView={isMobileView} heading={heading} />
          )}
          <Content
            className="content"
            style={{
              padding:
                isLoggedIn &&
                !isMobileView &&
                !location.pathname.includes('change-password') &&
                '12px 24px',
              backgroundColor: isMobileView
                ? '#FFFFFF'
                : 'var(--content-bg-color, #000000)',
              marginTop:
                isLoggedIn && !location.pathname.includes('change-password')
                  ? '54px'
                  : 0,
            }}
          >
            {isLoggedIn &&
              !isMobileView &&
              !location.pathname.includes('change-password') && (
                <Flex vertical gap={20}>
                  <AdminBreadcrumb />
                  <Flex justify="space-between" style={{ marginBottom: 4 }}>
                    <OrganisationChange />
                    <Text style={{ fontSize: 20 }} strong>
                      RideBuddy
                    </Text>
                  </Flex>
                </Flex>
              )}
            {children}
          </Content>
          {isLoggedIn && !isMobileView && (
            <Suspense fallback={null}>
              <SOSPopup />
              <DriverDrowsinessPopup />
              <AnomalySockets />
            </Suspense>
          )}
        </Layout>
      </Layout>
      {isLoggedIn && (
        <Suspense fallback={null}>
          <InactivityMonitor />
        </Suspense>
      )}
    </ConfigProvider>
  );
};

PageLayout.propTypes = {
  children: PropTypes.node,
};

export default React.memo(PageLayout);
